/**
 * Enhanced lazy loading for images using Intersection Observer
 * Only loads images when they are close to entering the viewport
 */

const DEFAULT_OPTIONS = {
  rootMargin: '200px 0px', // Start loading 200px before entering viewport
  threshold: 0.01,
};

/**
 * Initialize lazy loading for images
 * @param {string} selector - CSS selector for images to lazy load
 * @param {object} options - IntersectionObserver options
 */
export const initLazyLoad = (selector = 'img[loading="lazy"]', options = {}) => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    // Fallback for browsers without IntersectionObserver
    return;
  }

  const images = document.querySelectorAll(selector);
  if (!images.length) return;

  const observerOptions = { ...DEFAULT_OPTIONS, ...options };

  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;

        // Load the image
        if (img.dataset.src) {
          img.src = img.dataset.src;
        }

        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
        }

        // Remove lazy loading attribute
        img.removeAttribute('loading');

        // Stop observing this image
        observer.unobserve(img);

        // Add loaded class for potential CSS transitions
        img.classList.add('lazy-loaded');
      }
    });
  }, observerOptions);

  images.forEach(img => {
    imageObserver.observe(img);
  });

  return imageObserver;
};

/**
 * Initialize lazy loading specifically for game card images on homepage
 * Skip the first few images that should load immediately
 */
export const initHomePageLazyLoad = () => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return;
  }

  // Get all game rail sections
  const rails = document.querySelectorAll('[data-card-rail]');
  if (!rails.length) return;

  const observerOptions = {
    rootMargin: '300px 0px', // Start loading 300px before entering viewport
    threshold: 0.01,
  };

  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;

        // If image has data-lazy-src, load it
        if (img.dataset.lazySrc) {
          img.src = img.dataset.lazySrc;
          img.removeAttribute('data-lazy-src');
        }

        if (img.dataset.lazySrcset) {
          img.srcset = img.dataset.lazySrcset;
          img.removeAttribute('data-lazy-srcset');
        }

        // Change loading to eager now that it's visible
        img.setAttribute('loading', 'eager');

        observer.unobserve(img);
        img.classList.add('lazy-loaded');
      }
    });
  }, observerOptions);

  // Observe images in rails, but skip the first rail (Popular games)
  rails.forEach((rail, railIndex) => {
    // Only apply strict lazy loading to rails after the first one
    if (railIndex === 0) return;

    const images = rail.querySelectorAll('img[loading="lazy"]');
    images.forEach(img => {
      // Store original src in data attribute
      if (!img.dataset.lazySrc && img.src) {
        img.dataset.lazySrc = img.src;
        // Use a 1x1 transparent placeholder
        img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180"%3E%3C/svg%3E';
      }

      if (!img.dataset.lazySrcset && img.srcset) {
        img.dataset.lazySrcset = img.srcset;
        img.removeAttribute('srcset');
      }

      imageObserver.observe(img);
    });
  });

  return imageObserver;
};
